import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Copy,
  ImagePlus,
  IndianRupee,
  Smartphone,
  X,
} from 'lucide-react';
import { dataService, getErrorMessage } from '@/api';
import { AppButton, ModalShell } from '../../ui';
import { useToast } from '../../../hooks/useToast';
import { emitFeesChanged } from '../../../hooks/useFees';
import { copyToClipboard } from '../../../utils/clipboard';
import { cn } from '../../../utils/cn';
import {
  formatFeeDate,
  formatRupees,
  isFeeOverdue,
  isLikelyMobileDevice,
  isValidTxnId,
} from '../../../utils/upi';
import { FeeStatusBadge } from './FeeStatusBadge';
import { UpiQrCode } from './UpiQrCode';
import { SCREENSHOT_ACCEPT, screenshotToDataUrl } from './screenshotFile';

function CopyRow({ label, value, display, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
      <div className="min-w-0">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</p>
        <p className="truncate text-sm font-bold text-slate-900">{display ?? value}</p>
      </div>
      <button
        type="button"
        onClick={() => onCopy(value, label)}
        className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
        aria-label={`Copy ${label}`}
      >
        <Copy className="h-4 w-4" />
      </button>
    </div>
  );
}

function FeePicker({ fees, selectedId, onSelect, showStudent }) {
  if (fees.length < 2) return null;
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Open fees">
      {fees.map((f) => (
        <button
          key={f.id}
          type="button"
          role="tab"
          aria-selected={f.id === selectedId}
          onClick={() => onSelect(f.id)}
          className={cn(
            'shrink-0 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
            f.id === selectedId
              ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          )}
        >
          <span className="block font-black">{formatRupees(f.amount)}</span>
          <span className="block max-w-[10rem] truncate font-semibold">{f.title}</span>
          {showStudent && f.studentName ? (
            <span className="block max-w-[10rem] truncate text-[10px] text-slate-500">
              {f.studentName}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/**
 * Pay an open fee: scan QR / open UPI app, then submit the transaction ID
 * (required) and an optional screenshot for the admin to verify.
 */
export function FeePaymentModal({
  open,
  onClose,
  fees,
  settings,
  initialFeeId,
  showStudent = false,
  dismissLabel = 'Close',
}) {
  const { showToast } = useToast();
  const openFees = useMemo(
    () => (fees || []).filter((f) => f.status === 'pending' || f.status === 'submitted'),
    [fees]
  );
  const [selectedId, setSelectedId] = useState(initialFeeId || null);
  const [txnId, setTxnId] = useState('');
  const [txnTouched, setTxnTouched] = useState(false);
  const [shot, setShot] = useState(null);
  const [shotBusy, setShotBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSubmittedId, setJustSubmittedId] = useState(null);
  const fileRef = useRef(null);
  const isMobile = useMemo(() => isLikelyMobileDevice(), []);

  useEffect(() => {
    if (!open) return;
    setSelectedId((cur) => {
      if (initialFeeId && openFees.some((f) => f.id === initialFeeId)) return initialFeeId;
      if (cur && openFees.some((f) => f.id === cur)) return cur;
      return (openFees.find((f) => f.status === 'pending') || openFees[0])?.id || null;
    });
  }, [open, initialFeeId, openFees]);

  useEffect(() => {
    setTxnId('');
    setTxnTouched(false);
    setShot(null);
  }, [selectedId]);

  useEffect(() => {
    if (!open) setJustSubmittedId(null);
  }, [open]);

  const fee = openFees.find((f) => f.id === selectedId) || null;
  const txnValid = isValidTxnId(txnId);
  const canPay = !!settings?.enabled && !!fee?.upiLink;
  const overdue = isFeeOverdue(fee);

  const copy = async (value, label) => {
    const ok = await copyToClipboard(value);
    showToast(
      ok ? `${label} copied` : `Could not copy ${label.toLowerCase()}`,
      ok ? 'success' : 'error'
    );
  };

  const onPickScreenshot = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setShotBusy(true);
    try {
      const dataUrl = await screenshotToDataUrl(file);
      setShot({ name: file.name, dataUrl });
    } catch (err) {
      showToast(err?.message || 'Could not use that image', 'error');
    } finally {
      setShotBusy(false);
    }
  };

  const submit = async () => {
    if (!fee || !txnValid || submitting) return;
    setSubmitting(true);
    try {
      await dataService.submitFeePayment(fee.id, {
        txnId: txnId.trim(),
        screenshotDataUrl: shot?.dataUrl,
      });
      setJustSubmittedId(fee.id);
      showToast('Payment submitted — the admin will verify it shortly', 'success');
      emitFeesChanged();
    } catch (err) {
      showToast(getErrorMessage(err, 'Could not submit payment'), 'error');
      if (err?.response?.status === 409) emitFeesChanged();
    } finally {
      setSubmitting(false);
    }
  };

  const nextPending = openFees.find((f) => f.status === 'pending' && f.id !== justSubmittedId);

  return (
    <ModalShell open={open}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="fee-payment-title"
        data-fee-payment-modal
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <h3 id="fee-payment-title" className="text-lg font-black text-slate-900 sm:text-xl">
              {openFees.length > 1 ? `You have ${openFees.length} open fees` : 'Fee payment'}
            </h3>
            <p className="mt-0.5 text-sm font-medium text-slate-500">
              Pay via any UPI app, then enter the transaction ID so the admin can verify it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-xl p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
          <FeePicker
            fees={openFees}
            selectedId={selectedId}
            onSelect={(id) => {
              setJustSubmittedId(null);
              setSelectedId(id);
            }}
            showStudent={showStudent}
          />

          {!fee ? (
            <p className="py-8 text-center text-sm font-semibold text-slate-500">
              No open fees right now.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 p-4">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-700">{fee.title}</p>
                  {showStudent && fee.studentName ? (
                    <p className="text-xs font-semibold text-slate-500">For {fee.studentName}</p>
                  ) : null}
                  <p className="mt-1 text-3xl font-black tracking-tight text-slate-900">
                    {formatRupees(fee.amount)}
                  </p>
                  {fee.dueDate ? (
                    <p
                      className={cn(
                        'mt-1 text-xs font-bold',
                        overdue ? 'text-rose-700' : 'text-slate-500'
                      )}
                    >
                      {overdue ? 'Was due' : 'Due'} {formatFeeDate(fee.dueDate)}
                    </p>
                  ) : null}
                </div>
                <FeeStatusBadge status={fee.status} overdue={overdue} audience="payer" />
                {fee.note ? (
                  <p className="w-full text-xs font-medium text-slate-600">{fee.note}</p>
                ) : null}
              </div>

              {fee.status === 'submitted' || justSubmittedId === fee.id ? (
                <div className="flex gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sky-900">
                  {justSubmittedId === fee.id ? (
                    <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
                  ) : (
                    <Clock className="h-6 w-6 shrink-0" />
                  )}
                  <div className="min-w-0 text-sm">
                    <p className="font-black">
                      {justSubmittedId === fee.id
                        ? 'Submitted for verification'
                        : 'Awaiting verification'}
                    </p>
                    <p className="mt-1 font-medium">
                      The admin will check the payment in their bank / UPI app and mark it as paid.
                      You&apos;ll get a notification once it&apos;s confirmed.
                    </p>
                    {fee.txnId ? (
                      <p className="mt-2 font-mono text-xs font-bold">Txn ID: {fee.txnId}</p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  {fee.rejectionReason ? (
                    <div className="flex gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                      <AlertTriangle className="h-5 w-5 shrink-0" />
                      <p>
                        <span className="font-black">Previous payment not verified:</span>{' '}
                        {fee.rejectionReason}. Please check and submit the correct transaction ID.
                      </p>
                    </div>
                  ) : null}

                  {!canPay ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">
                      Online payment isn&apos;t set up yet. Please contact the admin to pay this
                      fee.
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start">
                      <div className="flex flex-col items-center gap-2">
                        <UpiQrCode value={fee.upiLink} size={196} />
                        <p className="text-center text-[11px] font-semibold text-slate-500">
                          Scan with GPay, PhonePe, Paytm, BHIM or any UPI app
                        </p>
                      </div>
                      <div className="min-w-0 space-y-2">
                        <p className="text-xs font-black uppercase tracking-wider text-slate-500">
                          Step 1 · Pay
                        </p>
                        {isMobile ? (
                          <a
                            href={fee.upiLink}
                            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-indigo-700"
                          >
                            <Smartphone className="h-5 w-5" />
                            Pay {formatRupees(fee.amount)} with UPI app
                          </a>
                        ) : null}
                        <CopyRow label="Pay to" value={settings.payeeName} onCopy={copy} />
                        <CopyRow label="UPI ID" value={settings.upiId} onCopy={copy} />
                        <CopyRow
                          label="Amount"
                          value={String(fee.amount)}
                          display={formatRupees(fee.amount)}
                          onCopy={copy}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-500">
                      Step 2 · Confirm payment
                    </p>
                    <div>
                      <label
                        htmlFor="fee-txn-id"
                        className="mb-1 block text-sm font-bold text-slate-700"
                      >
                        UPI transaction ID / UTR *
                      </label>
                      <input
                        id="fee-txn-id"
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        spellCheck={false}
                        value={txnId}
                        maxLength={64}
                        onChange={(e) => setTxnId(e.target.value)}
                        onBlur={() => setTxnTouched(true)}
                        placeholder="e.g. 412345678901"
                        className={cn(
                          'w-full rounded-xl border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2',
                          txnTouched && txnId && !txnValid
                            ? 'border-rose-400 focus:ring-rose-500'
                            : 'border-slate-300 focus:ring-indigo-500'
                        )}
                      />
                      <p
                        className={cn(
                          'mt-1 text-xs font-medium',
                          txnTouched && txnId && !txnValid ? 'text-rose-600' : 'text-slate-500'
                        )}
                      >
                        {txnTouched && txnId && !txnValid
                          ? 'Enter 6–35 letters or digits (spaces and dashes are ignored).'
                          : 'Find it in your UPI app under the payment details — usually a 12-digit "UPI Ref No." or "UTR".'}
                      </p>
                    </div>
                    <div>
                      <p className="mb-1 text-sm font-bold text-slate-700">
                        Payment screenshot{' '}
                        <span className="font-medium text-slate-400">(optional)</span>
                      </p>
                      <input
                        ref={fileRef}
                        type="file"
                        accept={SCREENSHOT_ACCEPT}
                        className="hidden"
                        onChange={onPickScreenshot}
                      />
                      {shot ? (
                        <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-2">
                          <img
                            src={shot.dataUrl}
                            alt="Payment screenshot preview"
                            className="h-14 w-14 rounded-lg object-cover"
                          />
                          <p className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-600">
                            {shot.name}
                          </p>
                          <button
                            type="button"
                            onClick={() => setShot(null)}
                            className="rounded-lg px-2 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={shotBusy}
                          onClick={() => fileRef.current?.click()}
                          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        >
                          <ImagePlus className="h-4 w-4" />
                          {shotBusy ? 'Preparing…' : 'Attach screenshot'}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <AppButton variant="secondary" onClick={onClose}>
            {dismissLabel}
          </AppButton>
          {fee && justSubmittedId === fee.id && nextPending ? (
            <AppButton
              onClick={() => {
                setJustSubmittedId(null);
                setSelectedId(nextPending.id);
              }}
            >
              Pay next fee
            </AppButton>
          ) : null}
          {fee && fee.status === 'pending' && justSubmittedId !== fee.id ? (
            <AppButton
              onClick={submit}
              disabled={!txnValid || submitting || shotBusy}
              title={txnValid ? undefined : 'Enter the transaction ID first'}
              data-fee-submit
            >
              <IndianRupee className="h-4 w-4" />
              {submitting ? 'Submitting…' : 'I have paid'}
            </AppButton>
          ) : null}
        </div>
      </div>
    </ModalShell>
  );
}
